import { Injectable } from '@angular/core';

import { CookieService } from 'ngx-cookie';

import { datetimeFormat } from 'app/constants/date-time-format';
import { SharedService } from 'app/services/shared.service';

import * as moment from 'moment';
import jwt_decode from 'jwt-decode';

@Injectable()
export class DateService {
  constructor(private cookieService: CookieService, private sharedService: SharedService) {
    moment().weekday(0);
  }

  jwtDecode(token) {
    return jwt_decode(token);
  }

  todayDate() {
    return moment();
  }

  previousDays(days = 1) {
    return moment().subtract(days, 'days');
  }

  previousDayFromDate(date, days = 1) {
    return moment(date).subtract(days, 'days');
  }

  dateSubtract(num = 1, value = 'days') {
    if (value === 'days') {
      return moment().subtract(num, 'days');
    } else if (value === 'weeks') {
      return moment().subtract(num, 'weeks');
    } else if (value === 'months') {
      return moment().subtract(num, 'months');
    } else if (value === 'hours') {
      return moment().subtract(num, 'hours');
    }
  }

  subtractPeriodFromDate(frequency = 1, period: any = 'days', date = new Date()) {
    // [hours, days, weeks, months]
    return moment(date).subtract(frequency, period);
  }

  getStartEndOfWeek(week) {
    return {
      start: moment().week(week).startOf('week').add(1, 'days'),
      end: moment().week(week).endOf('week').add(1, 'days'),
    };
  }

  getStartOfThisWeek(datetime: any = new Date(), format: string = datetimeFormat.mmmdyy) {
    return moment(new Date(datetime)).startOf('isoWeek').format(format);
  }

  getEndOfThisWeek(datetime: any = new Date(), format: string = datetimeFormat.mmmdyy) {
    return moment(new Date(datetime)).endOf('isoWeek').format(format);
  }

  getStartOfThisMonth(datetime: any = new Date(), format: string = datetimeFormat.mmmdyy) {
    return moment(datetime).startOf('month').format(format);
  }

  getEndOfThisMonth(datetime: any = new Date(), format: string = datetimeFormat.mmmdyy) {
    return moment(datetime).endOf('month').format(format);
  }

  getStartOfThisYear(datetime: any = new Date(), format: string = datetimeFormat.mmmdyy) {
    return moment(new Date(datetime)).startOf('year').format(format);
  }

  currentDate(days = 1) {
    return moment();
  }

  convertDate(date) {
    return moment(date);
  }

  getDay(date: string) {
    return moment(date).format('dddd');
  }

  getDayOfWeekNum(date: any) {
    return moment(date).day();
  }

  getDayByDayName(day: any) {
    return moment().day(day);
  }
  getTime(date) {
    return moment(date).format('h:mma');
  }

  startOfMonth(datetime: any, format: string = datetimeFormat.mmmdyyhhmmaa) {
    return moment(datetime).startOf('month').format(format);
  }

  endOfMonth(datetime: any, format: string = datetimeFormat.mmmdyyhhmmaa) {
    return moment(datetime).endOf('month').format(format);
  }

  startOfYear(datetime: any, format: string = datetimeFormat.mmmdyyhhmmaa) {
    return moment(datetime).startOf('year').format(format);
  }

  endOfYear(datetime: any, format: string = datetimeFormat.mmmdyyhhmmaa) {
    return moment(datetime).endOf('year').format(format);
  }

  isDST(datetime: any) {
    const millisecond = new Date(datetime).getTime();
    const dst0 = {
      start: new Date('2019-11-03 00:00').getTime(),
      end: new Date('2021-03-08 23:59').getTime(),
    };
    const dst1 = {
      start: new Date('2020-11-01 00:00').getTime(),
      end: new Date('2021-03-14 23:59').getTime(),
    };
    const dst2 = {
      start: new Date('2021-11-07 00:00').getTime(),
      end: new Date('2021-03-13 23:59').getTime(),
    };
    const dst3 = {
      start: new Date('2022-11-06 00:00').getTime(),
      end: new Date('2023-03-14 23:59').getTime(),
    };

    if (
      (millisecond > dst0.start && millisecond < dst0.end) ||
      (millisecond > dst1.start && millisecond < dst1.end) ||
      (millisecond > dst2.start && millisecond < dst2.end) ||
      (millisecond > dst3.start && millisecond < dst3.end)
    ) {
      return false;
    }
    return true;
  }

  getCurrentDateTimeTz(serverTime: number, timezoneAlt?: string, isApplyDTS?: boolean) {
    const { timezone: companyTz, isShopTzUseEnabled, isMultiLocationEnabled } = this.sharedService.getCompany();
    let timezone = timezoneAlt;

    if (isMultiLocationEnabled && isShopTzUseEnabled) {
      const shopTz = localStorage.getItem('currentShopTz');

      if (shopTz) {
        timezone = shopTz;
      }
    }

    if (!timezone) {
      timezone = companyTz;
    }

    if (isApplyDTS) {
      let timezoneValue: any = Number(timezone.replace('GMT', '').replace(':30', ''));
      let timeDST = this.isDST(serverTime);
      let currentDST = this.isDST(new Date());

      if (timeDST !== currentDST) {
        if (currentDST) {
          timezoneValue = timezoneValue - 1;
        } else {
          timezoneValue = timezoneValue + 1;
        }
      }

      timezone = `GMT${timezoneValue >= 0 ? '+' : ''}${timezoneValue}${timezone.includes(':30') ? ':30' : ''}`;
    }

    if (!timezone) {
      return new Date(serverTime);
    }

    const [hour, min] = timezone.replace('GMT', '').split(':');
    let offset = parseInt(hour, 10);

    if (min) {
      if (offset > 0) {
        offset += parseInt(min, 10) / 60;
      } else {
        offset -= parseInt(min, 10) / 60;
      }
    }

    const utc = serverTime + new Date(serverTime).getTimezoneOffset() * 60000;
    const date = new Date(utc + 3600000 * offset);

    return date;
  }

  madeDateStringIOSCompatible(date: string) {
    return date.replace(/-/g, '/');
  }

  addDaysInDatetime(datetime: any, amount: number, period: any, format: string = datetimeFormat.mmmdyyhhmmaa, isFormat: boolean = true) {
    const date = moment(new Date(datetime));

    if (isFormat) {
      return date.add(amount, period).format(format);
    } else {
      return date.add(amount, period).toDate();
    }
  }

  subDaysInDatetime(datetime: any, amount: number, period: any, format: string = datetimeFormat.mmmdyyhhmmaa) {
    const date = moment(new Date(datetime));
    return date.subtract(amount, period).format(format);
  }

  getCurrentDayTz(serverTime: number) {
    const date = this.getCurrentDateTimeTz(serverTime);

    return date.getDay();
  }

  getNearestSuccessorQuarterHour(minutes: any) {
    const quarter = (Math.round(Math.ceil(Number(minutes) / 15)) * 15) % 60;

    if (quarter < 9) {
      return `0${quarter}`;
    }

    return quarter;
  }

  getDateTimeByDateAndTime(date: any, time: any) {
    const newDate = moment(date).format('DD/MM/YYYY');
    const newDateTime = moment(newDate + time, 'DD/MM/YYYY HH:mm');

    return new Date(newDateTime.format('YYYY-MM-DD[T]HH:mm:ss'));
  }

  getDateTimeWithoutTimezone(datetime: any) {
    const datetimeM = moment(datetime);
    const date = datetimeM.format('YYYY-MM-DD');
    const time = `${datetimeM.format('HH')}:${this.getNearestSuccessorQuarterHour(datetimeM.format('mm'))}`;

    return `${date} ${time}`;
  }

  formatDate(date: any, format: string, currentLang?: string) {
    if (currentLang === 'fr') {
      return moment(new Date(date).getTime()).locale('fr').format(format);
    } else {
      return moment(new Date(date).getTime()).format(format);
    }
  }

  formatDateWithLocale(date: any, format: string, locale: string) {
    return moment(new Date(date).getTime()).locale(locale).format(format);
  }

  setLocale(locale: string) {
    moment.locale(locale);
  }

  get(format: any, date: Date | number) {
    return moment(date).get(format);
  }

  isValid(date: any) {
    return moment(new Date(date)).isValid();
  }

  formatDateRange(startDateTime: any, endDateTime: any, isNeedFormatting: boolean = false, currentLang?: string) {
    if (isNeedFormatting) {
      if (currentLang === 'fr') {
        startDateTime = this.formatDateWithLocale(new Date(startDateTime), datetimeFormat.mmmmdyyyy, 'fr');
        endDateTime = this.formatDateWithLocale(new Date(endDateTime), datetimeFormat.mmmmdyyyy, 'fr');
      } else {
        startDateTime = this.formatDate(new Date(startDateTime), datetimeFormat.mmmdyyyy);
        endDateTime = this.formatDate(new Date(endDateTime), datetimeFormat.mmmdyyyy);
      }
    }
    const [sMonth, sDay, sYear] = startDateTime.replace(',', '').split(' ');
    const [eMonth, eDay, eYear] = endDateTime.replace(',', '').split(' ');

    if (sMonth === eMonth && sYear === eYear) {
      return `${sMonth} ${sDay} - ${eDay}, ${sYear}`;
    } else if (sMonth !== eMonth && sYear === eYear) {
      return `${sMonth} ${sDay} - ${eMonth} ${eDay}, ${sYear}`;
    } else {
      return `${startDateTime} - ${endDateTime}`;
    }
  }

  formatEndDateRange(endDateTime: any, isNeedFormatting: boolean = false) {
    if (isNeedFormatting) {
      endDateTime = this.formatDate(new Date(endDateTime), datetimeFormat.mmmdyyyy);
    }
    const [eMonth, eDay, eYear] = endDateTime.replace(',', '').split(' ');

    return `${endDateTime}`;
  }

  isAppointmentWithInWorkingHour(settings: any, startDateTime: any, endDateTime: any) {
    if (!settings.isOpened) {
      return false;
    }

    const startTime = this.formatDate(startDateTime, datetimeFormat.hhmm);
    const endTime = this.formatDate(endDateTime, datetimeFormat.hhmm);

    if (startTime < settings.startHour || startTime > settings.endHour || endTime > settings.endHour) {
      return false;
    }

    return true;
  }

  getDateFromNumber(date, number) {
    return new Date(new Date(date).getFullYear(), new Date(date).getMonth(), number);
  }
}
